/**
 * TypeScript wrapper for the WASM PBD particle solver.
 *
 * Extracts world-space triangles from each caller-supplied THREE.Mesh,
 * packs the params into a Float64Array, invokes the Rust solver, and
 * reshapes the result into the `SettleOutput` the rest of the app expects.
 */

import * as THREE from "three/webgpu";
import type { SharedTriangleIndices } from "../modeling/types.ts";
import { EMPTY_SHARED_TRIANGLE_INDICES } from "../modeling/types.ts";
import {
  settle_particles as wasmSettleParticles,
  settle_particles_progress as wasmSettleParticlesProgress,
} from "./pkg/poseref_wasm.js";

// ---------------------------------------------------------------------------
// Public types — preserved from the original `modeling/physicsSim.ts` so the
// call sites in `app/store.ts` don't need to change.
// ---------------------------------------------------------------------------

export interface SettleInput {
  positions: Float64Array;
  edges: Uint32Array;
  pinned: Uint8Array;
  collisionMeshes: THREE.Mesh[];
  bendingEdges?: Uint32Array;
  triangles?: SharedTriangleIndices;
  recordTrajectory?: {
    stride: number;
    maxFrames: number;
  };
  params?: Partial<SettleParams>;
}

export interface SettleParams {
  dt: number;
  gravity: [number, number, number];
  maxSubsteps: number;
  innerIterations: number;
  damping: number;
  settleEpsilon: number;
  settleStreak: number;
  compliance: number;
  bendingCompliance: number;
  tautness: number;
  collisionSampleSpacing: number;
  selfCollisionThickness: number;
  selfCollisionWarmupSubsteps: number;
  settleExtensionSubsteps: number;
}

export interface SettleOutput {
  positions: Float64Array;
  substepsRun: number;
  maxEdgeDeviation: number;
  trajectory?: {
    frames: Float64Array;
    frameCount: number;
  };
}

export const DEFAULT_SETTLE_PARAMS: SettleParams = {
  dt: 1 / 60,
  gravity: [0, 0, -9.81],
  maxSubsteps: 400,
  innerIterations: 6,
  damping: 0.98,
  settleEpsilon: 1e-5,
  settleStreak: 5,
  compliance: 0,
  bendingCompliance: 1e-4,
  tautness: 1,
  collisionSampleSpacing: 0.05,
  selfCollisionThickness: 0,
  selfCollisionWarmupSubsteps: 20,
  settleExtensionSubsteps: 0,
};

const EMPTY_U32 = new Uint32Array(0);
const EMPTY_F32 = new Float32Array(0);

// ---------------------------------------------------------------------------
// Collision-mesh packing — flatten `THREE.Mesh[]` to a world-space triangle
// soup. Matches the assumption in surfaceRaycast.ts that each mesh's
// `matrixWorld` has already been set by the caller (buildCollisionMeshes).
// ---------------------------------------------------------------------------

interface PackedCollision {
  tris: Float32Array; // [ax,ay,az, bx,by,bz, cx,cy,cz, …] × triCount
  meshOffsets: Uint32Array; // length meshCount + 1
  aabbs: Float32Array; // [minX,minY,minZ, maxX,maxY,maxZ] × meshCount
}

function packCollisionMeshes(
  meshes: ReadonlyArray<THREE.Mesh>,
): PackedCollision {
  if (meshes.length === 0) {
    return { tris: EMPTY_F32, meshOffsets: EMPTY_U32, aabbs: EMPTY_F32 };
  }

  // Pass 1 — count triangles, build offsets, and accumulate AABBs.
  const meshOffsets = new Uint32Array(meshes.length + 1);
  const aabbs = new Float32Array(meshes.length * 6);
  let totalTris = 0;
  for (let m = 0; m < meshes.length; m++) {
    const geo = meshes[m].geometry;
    const idx = geo.getIndex();
    const triCount = idx ? (idx.count / 3) | 0 : 0;
    totalTris += triCount;
    meshOffsets[m + 1] = totalTris;
  }

  const tris = new Float32Array(totalTris * 9);

  // Pass 2 — emit world-space triangle vertices.
  const tmpA = new THREE.Vector3();
  const tmpB = new THREE.Vector3();
  const tmpC = new THREE.Vector3();
  let out = 0;
  for (let m = 0; m < meshes.length; m++) {
    const mesh = meshes[m];
    const geo = mesh.geometry;
    const pos = geo.getAttribute("position") as
      | THREE.BufferAttribute
      | undefined;
    const idx = geo.getIndex();
    const mat = mesh.matrixWorld;
    const abOff = m * 6;
    let minX = Infinity,
      minY = Infinity,
      minZ = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity,
      maxZ = -Infinity;

    if (!pos || !idx) {
      aabbs[abOff] = 0;
      aabbs[abOff + 1] = 0;
      aabbs[abOff + 2] = 0;
      aabbs[abOff + 3] = 0;
      aabbs[abOff + 4] = 0;
      aabbs[abOff + 5] = 0;
      continue;
    }

    const triCount = (idx.count / 3) | 0;
    for (let t = 0; t < triCount; t++) {
      const ia = idx.getX(t * 3);
      const ib = idx.getX(t * 3 + 1);
      const ic = idx.getX(t * 3 + 2);
      tmpA.fromBufferAttribute(pos, ia).applyMatrix4(mat);
      tmpB.fromBufferAttribute(pos, ib).applyMatrix4(mat);
      tmpC.fromBufferAttribute(pos, ic).applyMatrix4(mat);
      tris[out] = tmpA.x;
      tris[out + 1] = tmpA.y;
      tris[out + 2] = tmpA.z;
      tris[out + 3] = tmpB.x;
      tris[out + 4] = tmpB.y;
      tris[out + 5] = tmpB.z;
      tris[out + 6] = tmpC.x;
      tris[out + 7] = tmpC.y;
      tris[out + 8] = tmpC.z;
      out += 9;

      if (tmpA.x < minX) minX = tmpA.x;
      if (tmpA.x > maxX) maxX = tmpA.x;
      if (tmpA.y < minY) minY = tmpA.y;
      if (tmpA.y > maxY) maxY = tmpA.y;
      if (tmpA.z < minZ) minZ = tmpA.z;
      if (tmpA.z > maxZ) maxZ = tmpA.z;
      if (tmpB.x < minX) minX = tmpB.x;
      if (tmpB.x > maxX) maxX = tmpB.x;
      if (tmpB.y < minY) minY = tmpB.y;
      if (tmpB.y > maxY) maxY = tmpB.y;
      if (tmpB.z < minZ) minZ = tmpB.z;
      if (tmpB.z > maxZ) maxZ = tmpB.z;
      if (tmpC.x < minX) minX = tmpC.x;
      if (tmpC.x > maxX) maxX = tmpC.x;
      if (tmpC.y < minY) minY = tmpC.y;
      if (tmpC.y > maxY) maxY = tmpC.y;
      if (tmpC.z < minZ) minZ = tmpC.z;
      if (tmpC.z > maxZ) maxZ = tmpC.z;
    }

    if (triCount === 0) {
      minX = 0;
      minY = 0;
      minZ = 0;
      maxX = 0;
      maxY = 0;
      maxZ = 0;
    }
    aabbs[abOff] = minX;
    aabbs[abOff + 1] = minY;
    aabbs[abOff + 2] = minZ;
    aabbs[abOff + 3] = maxX;
    aabbs[abOff + 4] = maxY;
    aabbs[abOff + 5] = maxZ;
  }

  return { tris, meshOffsets, aabbs };
}

// ---------------------------------------------------------------------------
// Param packing
// ---------------------------------------------------------------------------

function packParams(overrides?: Partial<SettleParams>): Float64Array {
  const p: SettleParams = { ...DEFAULT_SETTLE_PARAMS, ...overrides };
  return new Float64Array([
    p.dt,
    p.gravity[0],
    p.gravity[1],
    p.gravity[2],
    p.maxSubsteps,
    p.innerIterations,
    p.damping,
    p.settleEpsilon,
    p.settleStreak,
    p.compliance,
    p.bendingCompliance,
    p.tautness,
    p.collisionSampleSpacing,
    p.selfCollisionThickness,
    p.selfCollisionWarmupSubsteps,
    p.settleExtensionSubsteps,
  ]);
}

// ---------------------------------------------------------------------------
// Pack / unpack — separated so the worker path can hand off raw typed
// arrays across the postMessage boundary without re-touching THREE.Mesh
// objects (which can't be transferred and don't belong inside a worker).
// ---------------------------------------------------------------------------

export interface PackedSettleRequest {
  positions: Float64Array;
  edges: Uint32Array;
  pinned: Uint8Array;
  bendingEdges: Uint32Array;
  triangles: SharedTriangleIndices;
  collisionTris: Float32Array;
  collisionMeshOffsets: Uint32Array;
  collisionAabbs: Float32Array;
  packedParams: Float64Array;
  recordStride: number;
  recordMaxFrames: number;
  recordStartSubstep: number;
  hasTrajectory: boolean;
}

export interface PackedSettleResult {
  positions: Float64Array;
  substepsRun: number;
  maxEdgeDeviation: number;
  trajectoryFrames: Float64Array;
  trajectoryFrameCount: number;
  hasTrajectory: boolean;
}

/** Bundle a `SettleInput` into transferable typed arrays. Triangle soup is
 *  collected here on the main thread because THREE.Mesh handles can't
 *  cross the postMessage boundary. */
export function packSettleInput(
  input: SettleInput,
  recordStartSubstep: number = 0,
): PackedSettleRequest {
  const packedParams = packParams(input.params);
  const bendingEdges = input.bendingEdges ?? EMPTY_U32;
  const triangles = input.triangles ?? EMPTY_SHARED_TRIANGLE_INDICES;
  const collision = packCollisionMeshes(input.collisionMeshes);

  const recordStride = input.recordTrajectory
    ? Math.max(1, Math.floor(input.recordTrajectory.stride))
    : 0;
  const recordMaxFrames = input.recordTrajectory
    ? Math.max(1, input.recordTrajectory.maxFrames)
    : 0;

  return {
    positions: input.positions,
    edges: input.edges,
    pinned: input.pinned,
    bendingEdges,
    triangles,
    collisionTris: collision.tris,
    collisionMeshOffsets: collision.meshOffsets,
    collisionAabbs: collision.aabbs,
    packedParams,
    recordStride,
    recordMaxFrames,
    recordStartSubstep: Math.max(0, Math.floor(recordStartSubstep)),
    hasTrajectory: input.recordTrajectory != null,
  };
}

/** All buffers in a `PackedSettleRequest` that are safe to hand to a
 *  Worker via the transferables list. After transfer, the originating
 *  thread MUST NOT access these buffers. */
export function packedSettleTransferables(
  req: PackedSettleRequest,
): Transferable[] {
  const buffers: Transferable[] = [];
  const addBuffer = (buffer: ArrayBufferLike) => {
    // Gravity regressed when we transferred shared empty sentinels like
    // `EMPTY_U32` / `EMPTY_SHARED_TRIANGLE_INDICES`: the first solve detached
    // them, and later wire-only solves quietly stopped reaching the worker.
    if (buffer.byteLength <= 0) return;
    if (buffer instanceof ArrayBuffer) buffers.push(buffer);
  };

  addBuffer(req.positions.buffer);
  addBuffer(req.edges.buffer);
  addBuffer(req.pinned.buffer);
  addBuffer(req.bendingEdges.buffer);
  addBuffer(req.triangles.buffer);
  addBuffer(req.collisionTris.buffer);
  addBuffer(req.collisionMeshOffsets.buffer);
  addBuffer(req.collisionAabbs.buffer);
  addBuffer(req.packedParams.buffer);

  return buffers;
}

/** Lift a worker-returned `PackedSettleResult` back into the
 *  `SettleOutput` shape every caller already understands. */
export function unpackSettleResult(packed: PackedSettleResult): SettleOutput {
  const out: SettleOutput = {
    positions: packed.positions,
    substepsRun: packed.substepsRun,
    maxEdgeDeviation: packed.maxEdgeDeviation,
  };
  if (packed.hasTrajectory) {
    out.trajectory = {
      frames: packed.trajectoryFrames,
      frameCount: packed.trajectoryFrameCount,
    };
  }
  return out;
}

/** Drive the WASM solver against a pre-packed request. Used both by the
 *  in-process `settleParticles` and by the physics worker. The optional
 *  `progressCallback` enables progress streaming + cooperative cancel
 *  via the `settle_particles_progress` Rust entry point. */
export function runWasmSettle(
  req: PackedSettleRequest,
  progressCallback?: (substep: number, total: number) => boolean,
  progressStride: number = 4,
): PackedSettleResult {
  const result = progressCallback
    ? wasmSettleParticlesProgress(
        req.positions,
        req.edges,
        req.pinned,
        req.bendingEdges,
        req.triangles,
        req.collisionTris,
        req.collisionMeshOffsets,
        req.collisionAabbs,
        req.packedParams,
        req.recordStride,
        req.recordMaxFrames,
        req.recordStartSubstep,
        progressCallback,
        Math.max(1, Math.floor(progressStride)),
      )
    : wasmSettleParticles(
        req.positions,
        req.edges,
        req.pinned,
        req.bendingEdges,
        req.triangles,
        req.collisionTris,
        req.collisionMeshOffsets,
        req.collisionAabbs,
        req.packedParams,
        req.recordStride,
        req.recordMaxFrames,
      );

  const substepsRun = result.substeps_run;
  const maxEdgeDeviation = result.max_edge_deviation;
  const frameCount = result.trajectory_frame_count;
  const trajectoryFlat = result.trajectory;
  result.free();

  return {
    positions: req.positions,
    substepsRun,
    maxEdgeDeviation,
    trajectoryFrames: trajectoryFlat,
    trajectoryFrameCount: frameCount,
    hasTrajectory: req.hasTrajectory,
  };
}

// ---------------------------------------------------------------------------
// Public entry point — synchronous, in-process. The async worker path
// lives in `physicsSimWorkerClient.ts` and reuses the helpers above.
// ---------------------------------------------------------------------------

export function settleParticles(input: SettleInput): SettleOutput {
  const req = packSettleInput(input);
  const packedResult = runWasmSettle(req);
  return unpackSettleResult(packedResult);
}
